import { AxiosError } from 'axios';

/**
 * 업무 오류가 <b>HTTP 200 + 본문</b>으로 내려오는 API 들(client-org 계열)에서, 호출부가
 * 본문의 `message` 를 담아 던지는 오류.
 *
 * 전용 타입인 이유: 이 값은 **사용자에게 그대로 보여도 되는 서버 문구**다. 평범한 `Error`
 * 로 던지면 axios 의 "Network Error" 같은 기술 문구와 구분할 수 없고, 실제로 그 둘을
 * 내용으로 가르려다 회귀를 냈다. 타입으로 가르면 그 실수가 불가능해진다.
 */
export class ApiBusinessError extends Error {
  /** 봉투의 `status`(contract 도메인은 400·401·500 등이 여기로 온다). 봉투 없이 던져지면 undefined. */
  readonly status?: number;
  /** 봉투의 `code`. 화면이 특정 거부를 골라내야 할 때 문구 대신 이것을 본다. */
  readonly code?: string;

  constructor(message: string, meta?: { status?: number; code?: string }) {
    super(message);
    this.name = 'ApiBusinessError';
    this.status = meta?.status;
    this.code = meta?.code;
  }
}

/**
 * 서버가 **권한 거절을 HTTP 401 로** 답했는가. 이 레포는 403 대신 401 을 준다(`UnauthorizedException`).
 * 요청에 `_keepSessionOn401` 을 단 호출만 로그아웃 없이 여기까지 온다 — 그 자리에서 「오류」 대신 사유를 말할 때 쓴다.
 *
 * 두 모양을 다 본다. ① 실제 HTTP 401(axios 오류의 `response.status`) ② 200 으로 온 실패 봉투의 status 401
 * (`ApiBusinessError.status`). 봉투로 401 을 내리는 도메인도 있어서 한쪽만 보면 조용히 뭉뚱그린다.
 */
export const isRejectedWith401 = (error: unknown): boolean => {
  const candidate = error as { status?: number; response?: { status?: number } } | null;
  return candidate?.response?.status === 401 || candidate?.status === 401;
};

/**
 * 서버 봉투의 `code`(예: `"04050"`). 화면이 특정 거절을 문구가 아니라 코드로 가를 때 쓴다.
 *
 * `isRejectedWith401` 과 같은 이유로 두 모양을 다 본다 — ① 실제 HTTP 오류의 본문(`response.data.code`)
 * ② 200 으로 온 실패 봉투를 호출부가 옮겨 담은 `ApiBusinessError.code`. 없으면 `undefined`.
 */
export const apiErrorCodeOf = (error: unknown): string | undefined => {
  const fromBody = (error as AxiosError<{ code?: unknown }> | null)?.response?.data?.code;
  if (typeof fromBody === 'string' && fromBody) return fromBody;
  if (error instanceof ApiBusinessError && error.code) return error.code;
  return undefined;
};

/**
 * 서버가 내려준 실패 사유를 꺼낸다.
 *
 * 백엔드는 core 의 ControllerExceptionAdvice 를 통해 `{ status, message, result }`
 * envelope 로 사유를 내려준다(예: "이미 취소된 작업기록입니다"). 반면 axios 의
 * `error.message` 는 "Request failed with status code 409" · "Network Error" 라서
 * 사용자에게 쓸모가 없다 — **그래서 어떤 경우에도 쓰지 않는다.**
 *
 * alert/토스트에는 반드시 이 함수를 거친 값을 쓴다.
 */
export const apiErrorMessage = (error: unknown, fallback = '요청을 처리하지 못했습니다.'): string => {
  const fromBody = (error as AxiosError<{ message?: string }>)?.response?.data?.message;
  if (fromBody?.trim()) return fromBody.trim();

  // 200 으로 내려온 업무 오류. 서버가 쓴 한국어 문구라 그대로 보여준다.
  if (error instanceof ApiBusinessError && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};
